import React, { useState } from "react";
import { Box, Typography, List, ListItemButton, ListItemText, ListItemIcon, IconButton, Button, Divider, Popper, Paper, ClickAwayListener, Badge, Chip, Collapse, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Link from "next/link";
import { purple, green, blue, orange, pink, rose, gray } from "@/theme/colors";
import { getCurrentUserId, getAllNewlyAdded, getAllRejected, addRejected, getRejected, setNotificationsLastSeen, getNotificationsLastSeen, setCreatePrefill, resetClientState } from "@/utils/localStore";
import { buildOrganizationRouteId } from "@/utils/orgRoutes";
import { assetUrl } from "@/utils/asset";
import { useAuth } from "@/context/AuthContext";
import { ProfileDetailsModal } from "../Profile/ProfileDetailsModal";
import changelogData from "@/data/changelog.json";
import { useGetMySubmissionsQuery } from "@/src/services/organizationsApi";
import { useGetNotificationsQuery, useHideNotificationMutation, useHideAllNotificationsMutation } from "@/src/services/notificationsApi";
import { useSmartPollingInterval } from "@/hooks/useSmartPollingInterval";

// Notifications popover item shape (shared between the popover + "all" dialog).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Shared account/profile dropdown subsystem used by both the legacy `NavBar`
 * and the `SearchNavBar`. Encapsulates notifications (badge + popover + "view
 * all" dialog), the changelog dialog, the profile details modal and the
 * refresh-data action so both navbars behave identically from a single source.
 */
export function useAccountMenu() {
  const smartPolling = useSmartPollingInterval(300_000); // 5 min base

  const navigate = path => {
    if (typeof window !== "undefined") {
      try {
        const href = assetUrl(path);
        window.location.assign(href);
      } catch {
        // noop
      }
    }
  };
  const {
    isAuthenticated,
    logout,
    user,
    token,
    profile,
    profileModalOpen,
    profileUpdating,
    profileError,
    openProfileModal,
    closeProfileModal,
    refreshUser,
    updateProfile
  } = useAuth();
  const isAdmin = user?.role === "admin";

  // ── Changelog dialog state ──
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [expandedDates, setExpandedDates] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const toggleDate = key => setExpandedDates(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  const toggleSection = key => setExpandedSections(prev => ({
    ...prev,
    [key]: !prev[key]
  }));
  const changelogTypeColor = {
    organizations: orange.primary,
    events: green.primary,
    regulations: blue.primary,
    articles: purple.primary,
    professionals: rose.primary,
    tasks: rose.primary,
    "Task Manager": rose.primary
  };
  const getTypeColor = pageType => changelogTypeColor[pageType] || pink.primary;
  const profileDetails = React.useMemo(() => profile ? {
    firstName: profile.profile?.firstName || "",
    lastName: profile.profile?.lastName || "",
    city: profile.profile?.location?.city || "",
    state: profile.profile?.location?.state || "",
    country: profile.profile?.location?.country || "",
    timezone: profile.profile?.timezone || ""
  } : null, [profile?.profile?.firstName, profile?.profile?.lastName, profile?.profile?.location?.city, profile?.profile?.location?.state, profile?.profile?.location?.country, profile?.profile?.timezone]);
  const {
    data: mySubmissions = []
  } = useGetMySubmissionsQuery(undefined, {
    skip: !token,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: smartPolling
  });
  const handleProfileImageUpdated = React.useCallback(async () => {
    try {
      await refreshUser();
    } catch {
      // best-effort refresh so avatar changes are reflected quickly
    }
  }, [refreshUser]);
  React.useEffect(() => {
    if (!token || !user?.id) return;
    if (!Array.isArray(mySubmissions) || mySubmissions.length === 0) {
      return;
    }
    const existing = getRejected("organization");
    const existingIds = new Set(existing.map(r => String(r.id)));
    mySubmissions.forEach(submission => {
      const status = String(submission.status || "").toLowerCase();
      if (status !== "rejected") return;
      if (submission.submittedByUserId && submission.submittedByUserId !== user.id) {
        return;
      }
      const id = String(submission.id || "").trim();
      if (!id || existingIds.has(id)) return;
      const payload = submission.payload || {};
      const rejectionTimestamp = submission.reviewedAt ? new Date(submission.reviewedAt).getTime() : submission.updatedAt ? new Date(submission.updatedAt).getTime() : submission.createdAt ? new Date(submission.createdAt).getTime() : Date.now();
      try {
        addRejected("organization", {
          id,
          type: "organization",
          payload: {
            ...payload,
            id: payload.id || id,
            slug: payload.slug || payload.id || id
          },
          rejectionTimestamp,
          rejectedBy: submission.reviewedByUserId || "admin",
          createdBy: submission.submittedByUserId || user.id
        });
        existingIds.add(id);
      } catch {
        // Ignore storage errors or cookie consent blocks
      }
    });
  }, [mySubmissions, token, user?.id]);
  const [notifCount, setNotifCount] = React.useState(0);
  const userIdRef = React.useRef(null);

  // Notifications popover state
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const profileMenuOpen = Boolean(profileAnchorEl);
  const [notifItems, setNotifItems] = useState([]);
  const notifOpen = Boolean(notifAnchorEl);
  const [allDialogOpen, setAllDialogOpen] = useState(false);
  const [allItems, setAllItems] = useState([]);

  // ── Backend-driven notifications ──
  const useBackend = !!token;
  const {
    data: backendNotifs = [],
    refetch: refetchBackendNotifs
  } = useGetNotificationsQuery(undefined, {
    skip: !useBackend,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: smartPolling
  });
  const [hideNotification] = useHideNotificationMutation();
  const [hideAllNotifications] = useHideAllNotificationsMutation();
  const backendToNotifItem = React.useCallback(n => ({
    id: n.id,
    kind: n.kind,
    type: n.type,
    title: n.title,
    ts: new Date(n.createdAt).getTime(),
    href: n.href || undefined,
    operation: n.operation,
    payload: n.meta?.payload,
    reason: n.meta?.reason,
    createdBy: n.meta?.createdBy,
    submissionId: n.meta?.submissionId,
    notifStatus: n.status,
    deactivationReason: n.meta?.deactivationReason
  }), []);
  const backendNewNotifs = React.useMemo(() => useBackend ? backendNotifs.filter(n => n.status === "new").map(backendToNotifItem) : [], [useBackend, backendNotifs, backendToNotifItem]);
  const backendNewCount = backendNewNotifs.length;
  const backendAllNotifs = React.useMemo(() => useBackend ? [...backendNotifs.filter(n => n.status === "new").map(backendToNotifItem), ...backendNotifs.filter(n => n.status === "hidden").map(backendToNotifItem)] : [], [useBackend, backendNotifs, backendToNotifItem]);
  const renderSecondary = React.useCallback(item => {
    const baseText = item.title + " • " + new Date(item.ts).toLocaleString();
    const outdatedChip = item.outdated ? /*#__PURE__*/_jsx(Chip, {
      label: "Outdated",
      size: "small",
      variant: "outlined",
      sx: {
        alignSelf: "flex-start",
        mt: 0.5
      }
    }) : null;
    const truncateReason = s => s.length > 50 ? `${s.slice(0, 50).trimEnd()}…` : s;
    const rejectionLine = item.kind === "rejected" && item.reason ? /*#__PURE__*/_jsx(Typography, {
      variant: "caption",
      color: "error.main",
      component: "span",
      sx: {
        mt: 0.25
      },
      children: `Rejection message: ${truncateReason(item.reason)}`
    }) : null;
    return /*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "flex",
        flexDirection: "column"
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "body2",
        color: "text.secondary",
        component: "span",
        children: baseText
      }), rejectionLine, outdatedChip]
    });
  }, []);
  const renderNotifContent = React.useCallback(item => {
    const isDeactivation = item.operation === "deactivate";
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ListItemIcon, {
        sx: {
          minWidth: 32
        },
        children: item.kind === "approved" && !isDeactivation ? /*#__PURE__*/_jsx(CheckCircleOutlineIcon, {
          color: "success",
          fontSize: "small"
        }) : /*#__PURE__*/_jsx(HighlightOffIcon, {
          color: "error",
          fontSize: "small"
        })
      }), /*#__PURE__*/_jsx(ListItemText, {
        primary: `${item.kind === "approved" ? isDeactivation ? "Approved deactivation" : "Approved" : isDeactivation ? "Rejected deactivation" : "Rejected"} ${isDeactivation ? "" : item.type}`.trim(),
        secondary: renderSecondary(item),
        secondaryTypographyProps: {
          component: "div"
        }
      })]
    });
  }, [renderSecondary]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const getGroupKey = React.useCallback(item => {
    const payload = item.payload || {};
    const history = Array.isArray(payload.submissionHistory) ? payload.submissionHistory : [];
    const historyRoot = history[0];
    const base = historyRoot || payload.previousSubmissionId || payload.submissionId || payload.rootSubmissionId || payload.slug || payload.id || payload.orgName || payload.name || payload.title || payload.shortTitle || item.title || item.id;
    return `${item.type}:${String(base || "").toLowerCase()}`;
  }, []);
  const groupNotifications = React.useCallback(items => {
    const grouped = new Map();
    items.forEach(item => {
      const key = getGroupKey(item);
      const list = grouped.get(key) || [];
      list.push({
        ...item,
        groupKey: key
      });
      grouped.set(key, list);
    });
    const result = [];
    grouped.forEach(list => {
      const sorted = [...list].sort((a, b) => b.ts - a.ts);
      const [latest, ...rest] = sorted;
      const children = rest.map(entry => ({
        ...entry,
        outdated: true
      }));
      result.push({
        ...latest,
        outdated: false,
        children
      });
    });
    return result.sort((a, b) => b.ts - a.ts);
  }, [getGroupKey]);

  // Map a payload to a user-friendly title and optional href
  const mapTitleAndHref = (type, payload) => {
    switch (type) {
      case "organization":
        {
          const slug = payload?.slug;
          const title = payload?.orgName || payload?.title || slug || "Item";
          return {
            title,
            href: slug ? `/organizations/${slug}` : undefined
          };
        }
      case "event":
        {
          const slug = payload?.slug;
          const title = payload?.header || payload?.title || slug || "Event";
          return {
            title,
            href: slug ? `/events/${slug}` : undefined
          };
        }
      case "regulation":
        {
          const slug = payload?.slug;
          const rid = slug || payload?.id;
          const title = payload?.title || rid || "Regulation";
          return {
            title,
            href: rid ? `/regulations/${rid}` : undefined
          };
        }
      case "article":
        {
          const slug = payload?.slug;
          const rid = slug || payload?.id;
          const title = payload?.title || rid || "Article";
          return {
            title,
            href: rid ? `/articles/${rid}` : undefined
          };
        }
      default:
        {
          const title = payload?.title || payload?.id || "Item";
          return {
            title
          };
        }
    }
  };
  const getUnreadThreadCount = React.useCallback(() => {
    const userId = getCurrentUserId();
    const lastSeen = getNotificationsLastSeen(userId);
    const matchesScope = creator => isAdmin || !!creator && creator === userId;
    const approvalsAll = getAllNewlyAdded().filter(a => matchesScope(a.createdBy));
    const approvals = approvalsAll.filter(a => a.approvalTimestamp > lastSeen);
    const rejections = getAllRejected().filter(r => r.rejectionTimestamp > lastSeen).filter(r => matchesScope(r.createdBy));
    const items = [...approvals.map(a => {
      const {
        title
      } = mapTitleAndHref(a.type, a.payload);
      return {
        id: a.id,
        kind: "approved",
        type: a.type,
        title,
        ts: a.approvalTimestamp,
        payload: a.payload,
        createdBy: a.createdBy,
        operation: a.operation
      };
    }), ...rejections.map(r => {
      const {
        title
      } = mapTitleAndHref(r.type, r.payload);
      return {
        id: r.id,
        kind: "rejected",
        type: r.type,
        title,
        ts: r.rejectionTimestamp,
        payload: r.payload,
        reason: r.reason,
        createdBy: r.createdBy,
        submissionId: r.submissionId || r.id
      };
    })];
    return groupNotifications(items).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupNotifications, isAdmin]);
  React.useEffect(() => {
    // When backend is available, use backend count; otherwise localStorage
    if (useBackend) {
      setNotifCount(backendNewCount);
      return;
    }
    const id = getCurrentUserId();
    userIdRef.current = id;
    const refreshCount = () => {
      setNotifCount(getUnreadThreadCount());
    };
    refreshCount();
    if (typeof window !== "undefined") {
      window.addEventListener("pc-notifications-changed", refreshCount);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("pc-notifications-changed", refreshCount);
      }
    };
  }, [getUnreadThreadCount, useBackend, backendNewCount]);
  const buildNotifications = () => {
    // Backend-driven: show "new" notifications
    if (useBackend) {
      refetchBackendNotifs();
      setNotifItems(backendNewNotifs);
      return;
    }
    // Fallback: localStorage-based
    const userId = getCurrentUserId();
    const lastSeen = getNotificationsLastSeen(userId);
    const matchesScope = creator => isAdmin || !!creator && creator === userId;
    // Show all approvals/rejections since last seen within scope
    const approvalsAll = getAllNewlyAdded().filter(a => matchesScope(a.createdBy));
    const approvals = approvalsAll.filter(a => a.approvalTimestamp > lastSeen);
    const rejections = getAllRejected().filter(r => r.rejectionTimestamp > lastSeen).filter(r => matchesScope(r.createdBy));
    const items = [...approvals.map(a => {
      const {
        title,
        href
      } = mapTitleAndHref(a.type, a.payload);
      let resolvedHref = href;
      if (a.type === "regulation" || a.type === "article") {
        const rid = a.payload?.slug || a.payload?.id || String(a.approvalTimestamp);
        resolvedHref = a.type === "article" ? `/articles/${rid}` : `/regulations/${rid}`;
      } else if (a.type === "organization") {
        const p = a.payload || {};
        const rid = buildOrganizationRouteId(p, {
          existing: []
        });
        const base = String(p.slug || p.id || "");
        resolvedHref = `/organizations/${rid || base}`;
      }
      return {
        id: a.id,
        kind: "approved",
        type: a.type,
        title,
        ts: a.approvalTimestamp,
        href: resolvedHref,
        createdBy: a.createdBy,
        operation: a.operation
      };
    }), ...rejections.map(r => {
      const {
        title,
        href
      } = mapTitleAndHref(r.type, r.payload);
      return {
        id: r.id,
        kind: "rejected",
        type: r.type,
        title,
        ts: r.rejectionTimestamp,
        href,
        payload: r.payload,
        reason: r.reason,
        createdBy: r.createdBy
      };
    })].sort((a, b) => b.ts - a.ts);
    setNotifItems(groupNotifications(items));
  };
  const buildAllNotifications = () => {
    // Backend-driven: show all, new first then hidden
    if (useBackend) {
      refetchBackendNotifs();
      setAllItems(backendAllNotifs);
      return;
    }
    // Fallback: localStorage-based
    const userId = getCurrentUserId();
    const matchesScope = creator => isAdmin || !!creator && creator === userId;
    const approvals = getAllNewlyAdded().filter(a => matchesScope(a.createdBy));
    const rejections = getAllRejected().filter(r => matchesScope(r.createdBy));
    const items = [...approvals.map(a => {
      const {
        title
      } = mapTitleAndHref(a.type, a.payload);
      let resolvedHref = undefined;
      if (a.type === "regulation" || a.type === "article") {
        const rid = a.payload?.slug || a.payload?.id || String(a.approvalTimestamp);
        resolvedHref = a.type === "article" ? `/articles/${rid}` : `/regulations/${rid}`;
      } else if (a.type === "organization") {
        const p = a.payload || {};
        const rid = buildOrganizationRouteId(p, {
          existing: []
        });
        const base = String(p.slug || p.id || "");
        resolvedHref = `/organizations/${rid || base}`;
      } else {
        const {
          href
        } = mapTitleAndHref(a.type, a.payload);
        resolvedHref = href;
      }
      return {
        id: a.id,
        kind: "approved",
        type: a.type,
        title,
        ts: a.approvalTimestamp,
        href: resolvedHref,
        createdBy: a.createdBy,
        operation: a.operation
      };
    }), ...rejections.map(r => {
      const {
        title,
        href
      } = mapTitleAndHref(r.type, r.payload);
      return {
        id: r.id,
        kind: "rejected",
        type: r.type,
        title,
        ts: r.rejectionTimestamp,
        href,
        payload: r.payload,
        reason: r.reason,
        createdBy: r.createdBy
      };
    })].sort((a, b) => b.ts - a.ts);
    setAllItems(groupNotifications(items));
  };
  const handleNotificationsClose = () => setNotifAnchorEl(null);
  const handleNotificationsClear = () => {
    if (useBackend) {
      hideAllNotifications().unwrap().then(() => refetchBackendNotifs()).catch(() => {});
      setNotifCount(0);
      setNotifItems([]);
      setNotifAnchorEl(null);
      return;
    }
    const id = userIdRef.current || getCurrentUserId();
    // Mark as seen up to "now"
    setNotificationsLastSeen(id, Date.now());
    setNotifCount(0);
    setNotifItems([]);
    setNotifAnchorEl(null);
  };
  const handleViewAll = () => {
    buildAllNotifications();
    setAllDialogOpen(true);
  };
  const handleCloseAllDialog = () => setAllDialogOpen(false);
  const handleProfileMenuOpen = e => {
    setProfileAnchorEl(e.currentTarget);
  };
  const handleProfileMenuClose = () => setProfileAnchorEl(null);
  const handleProfileMenuSelect = action => {
    if (action === "notifications") {
      // open notifications popper anchored to profile icon
      buildNotifications();
      setNotifAnchorEl(profileAnchorEl);
      handleProfileMenuClose();
      return;
    }
    if (action === "changelog") {
      setChangelogOpen(true);
      handleProfileMenuClose();
      return;
    }
    if (action === "refresh-data") {
      // True client-state reset: wipe every pc_* / dataVersion:* key
      // (caches, dev flags, deactivated org list, saved filters,
      // newly-added overlays, etc.) while preserving auth + cookie
      // consent so the user stays logged in. Then hard-reload so
      // RTK Query, hydration, and JSS class numbering all start clean.
      resetClientState();
      handleProfileMenuClose();
      if (typeof window !== "undefined") {
        window.location.reload();
      }
      return;
    }
    if (action === "logout") {
      logout();
      handleProfileMenuClose();
      if (typeof window !== "undefined") {
        window.location.reload();
      }
      return;
    }
    handleProfileMenuClose();
    if (action === "login") {
      navigate("/login");
    } else if (action === "profile") {
      openProfileModal();
    } else {
      alert("Coming soon");
    }
  };

  // Shared outdated/dismiss overlay used by every notification row.
  const overlay = (className, label) => /*#__PURE__*/_jsx(Box, {
    className: className,
    sx: {
      position: "absolute",
      inset: 0,
      bgcolor: "rgba(0,0,0,0.04)",
      opacity: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
      transition: "opacity 0.2s ease"
    },
    children: /*#__PURE__*/_jsx(Typography, {
      variant: "caption",
      color: "text.secondary",
      sx: {
        bgcolor: "background.paper",
        px: 1,
        py: 0.5,
        borderRadius: 1,
        border: t => `1px solid ${t.palette.divider}`
      },
      children: label
    })
  });

  // Single notification-row renderer shared by the popover ("popover") and the
  // "view all" dialog ("dialog"). The two modes differ only in keys, the
  // "Dismissed" section header, hidden-item opacity, the dismiss overlay and
  // which close handler / state update fires on click — all encoded below.
  const renderNotifRow = (n, ctx) => {
    const {
      mode,
      index,
      list
    } = ctx;
    const isDialog = mode === "dialog";
    const key = isDialog ? `all-${n.kind}-${n.id}` : `${n.kind}-${n.id}`;
    const isHidden = isDialog && useBackend && n.notifStatus === "hidden";
    const showDismissedHeader = isDialog && useBackend && isHidden && (index === 0 || list[index - 1].notifStatus !== "hidden");
    const hasChildren = Array.isArray(n.children) && n.children.length > 0;
    const groupKey = n.groupKey || key;
    const isExpanded = hasChildren && !!expandedGroups[groupKey];
    const toggleGroup = e => {
      e.stopPropagation();
      setExpandedGroups(prev => ({
        ...prev,
        [groupKey]: !prev[groupKey]
      }));
    };
    const dismissedHeader = showDismissedHeader ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Divider, {
        sx: {
          my: 1
        }
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "overline",
        sx: {
          px: 2,
          display: "block",
          color: "text.secondary"
        },
        children: "Dismissed"
      })]
    }) : null;
    const olderButton = hasChildren ? /*#__PURE__*/_jsx(Button, {
      size: "small",
      onClick: toggleGroup,
      sx: {
        ml: "auto"
      },
      children: isExpanded ? "Hide" : `Older (${n.children?.length})`
    }) : null;
    const childrenCollapse = hasChildren ? /*#__PURE__*/_jsx(Collapse, {
      in: isExpanded,
      timeout: "auto",
      unmountOnExit: true,
      children: /*#__PURE__*/_jsx(List, {
        dense: true,
        disablePadding: true,
        children: n.children?.map(child => {
          const childKey = `${key}-child-${child.id}`;
          return /*#__PURE__*/_jsx(ListItemButton, {
            sx: {
              pl: 6,
              position: "relative",
              cursor: "not-allowed",
              opacity: 0.7
            },
            children: renderNotifContent(child)
          }, childKey);
        })
      })
    }) : null;
    const baseOpacity = n.outdated ? 0.75 : isHidden ? 0.55 : 1;

    // ── Rejected: routes to the create/edit flow ──
    if (n.kind === "rejected") {
      const onClick = () => {
        if (n.outdated) return;
        if (isDialog && useBackend) {
          hideNotification(n.id).catch(() => {});
        }
        if (n.operation === "deactivate" && n.href) {
          navigate(n.href);
          if (!isDialog && useBackend) {
            hideNotification(n.id).catch(() => {});
          }
          if (isDialog) handleCloseAllDialog();else setNotifAnchorEl(null);
          return;
        }
        setCreatePrefill({
          type: n.type,
          payload: n.payload,
          reason: n.reason,
          ...(isDialog ? {} : {
            submissionId: n.submissionId || n.id
          })
        });
        if (n.type === "organization") {
          const id = n.payload?.id;
          const slug = n.payload?.slug;
          const q = new URLSearchParams({
            ...(id ? {
              editId: String(id)
            } : {}),
            ...(slug ? {
              editSlug: String(slug)
            } : {})
          });
          navigate(`/organizations/create?${q.toString()}`);
        } else if (n.type === "event") {
          navigate("/events/create");
        } else if (n.type === "regulation") {
          navigate("/regulations/create");
        } else if (n.type === "article") {
          navigate("/articles/create");
        } else {
          navigate("/");
        }
        if (!isDialog && useBackend) {
          hideNotification(n.id).catch(() => {});
        }
        if (isDialog) handleCloseAllDialog();else setNotifAnchorEl(null);
      };
      return /*#__PURE__*/_jsxs(React.Fragment, {
        children: [dismissedHeader, /*#__PURE__*/_jsxs(ListItemButton, {
          onClick: onClick,
          "aria-disabled": n.outdated,
          sx: {
            position: "relative",
            cursor: n.outdated ? "not-allowed" : "pointer",
            opacity: baseOpacity,
            "&:hover .pc-outdated-overlay": {
              opacity: n.outdated ? 1 : 0
            }
          },
          children: [renderNotifContent(n), olderButton, n.outdated && overlay("pc-outdated-overlay", "Outdated")]
        }), childrenCollapse]
      }, key);
    }

    // ── No href (or outdated): dismiss-only ──
    if (!n.href || n.outdated) {
      const isDismissable = !n.outdated && useBackend;
      const onClick = e => {
        if (n.outdated) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (useBackend) {
          hideNotification(n.id).unwrap().then(() => {
            refetchBackendNotifs();
            if (isDialog) {
              setAllItems(prev => prev.map(x => x.id === n.id ? {
                ...x,
                notifStatus: "hidden"
              } : x));
            } else {
              setNotifItems(prev => prev.filter(x => x.id !== n.id));
            }
          }).catch(() => {});
        }
        if (isDialog) handleCloseAllDialog();
      };
      return /*#__PURE__*/_jsxs(React.Fragment, {
        children: [dismissedHeader, /*#__PURE__*/_jsxs(ListItemButton, {
          "aria-disabled": n.outdated,
          onClick: onClick,
          sx: {
            position: "relative",
            cursor: n.outdated ? "not-allowed" : isDismissable ? "pointer" : "default",
            opacity: baseOpacity,
            "&:hover .pc-outdated-overlay": {
              opacity: n.outdated ? 1 : 0
            },
            ...(!isDialog ? {
              "&:hover .pc-dismiss-overlay": {
                opacity: isDismissable ? 1 : 0
              }
            } : {})
          },
          children: [renderNotifContent(n), olderButton, n.outdated && overlay("pc-outdated-overlay", "Outdated"), !isDialog && isDismissable && overlay("pc-dismiss-overlay", "Dismiss")]
        }), childrenCollapse]
      }, key);
    }

    // ── Has href: navigates to the item ──
    const linkProps = n.href ? {
      component: Link,
      href: n.href
    } : {
      component: "div"
    };
    const onClick = e => {
      if (n.outdated) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (useBackend) {
        hideNotification(n.id).catch(() => {});
      }
      if (isDialog) handleCloseAllDialog();else setNotifAnchorEl(null);
    };
    return /*#__PURE__*/_jsxs(React.Fragment, {
      children: [dismissedHeader, /*#__PURE__*/_jsxs(ListItemButton, {
        ...linkProps,
        onClick: onClick,
        "aria-disabled": n.outdated,
        sx: {
          position: "relative",
          cursor: n.outdated ? "not-allowed" : "pointer",
          opacity: baseOpacity,
          "&:hover .pc-outdated-overlay": {
            opacity: n.outdated ? 1 : 0
          }
        },
        children: [renderNotifContent(n), olderButton, n.outdated && overlay("pc-outdated-overlay", "Outdated")]
      }), childrenCollapse]
    }, key);
  };
  const elements = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Popper, {
      open: profileMenuOpen,
      anchorEl: profileAnchorEl,
      placement: "bottom-end",
      style: {
        zIndex: 1600
      },
      children: /*#__PURE__*/_jsx(ClickAwayListener, {
        onClickAway: handleProfileMenuClose,
        children: /*#__PURE__*/_jsx(Paper, {
          sx: {
            py: 1,
            minWidth: 180
          },
          children: /*#__PURE__*/_jsxs(List, {
            dense: true,
            sx: {
              p: 0
            },
            children: [isAuthenticated && /*#__PURE__*/_jsxs(ListItemButton, {
              onClick: () => handleProfileMenuSelect("notifications"),
              children: [/*#__PURE__*/_jsx(ListItemText, {
                primary: "Notifications"
              }), notifCount > 0 && /*#__PURE__*/_jsx(Badge, {
                color: "error",
                badgeContent: notifCount,
                sx: {
                  ml: 1
                }
              })]
            }), isAuthenticated && user?.role === "admin" && /*#__PURE__*/_jsx(ListItemButton, {
              onClick: () => {
                navigate("/Admin");
                handleProfileMenuClose();
              },
              children: /*#__PURE__*/_jsx(ListItemText, {
                primary: "Admin"
              })
            }), isAuthenticated && user?.role === "admin" && /*#__PURE__*/_jsx(ListItemButton, {
              onClick: () => handleProfileMenuSelect("changelog"),
              children: /*#__PURE__*/_jsx(ListItemText, {
                primary: "Changelog"
              })
            }), isAuthenticated && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(ListItemButton, {
                onClick: () => handleProfileMenuSelect("profile"),
                children: /*#__PURE__*/_jsx(ListItemText, {
                  primary: "Profile"
                })
              }), /*#__PURE__*/_jsx(ListItemButton, {
                onClick: () => handleProfileMenuSelect("refresh-data"),
                children: /*#__PURE__*/_jsx(ListItemText, {
                  primary: "Refresh Data"
                })
              }), /*#__PURE__*/_jsx(Divider, {
                sx: {
                  my: 0.5
                }
              })]
            }), !isAuthenticated && /*#__PURE__*/_jsx(Divider, {
              sx: {
                my: 0.5
              }
            }), isAuthenticated ? /*#__PURE__*/_jsx(ListItemButton, {
              onClick: () => handleProfileMenuSelect("logout"),
              children: /*#__PURE__*/_jsx(ListItemText, {
                primary: "Logout"
              })
            }) : /*#__PURE__*/_jsx(ListItemButton, {
              onClick: () => handleProfileMenuSelect("login"),
              children: /*#__PURE__*/_jsx(ListItemText, {
                primary: "Log in / Sign up"
              })
            })]
          })
        })
      })
    }), /*#__PURE__*/_jsx(Popper, {
      open: notifOpen,
      anchorEl: notifAnchorEl,
      placement: "bottom-end",
      style: {
        zIndex: 1500
      },
      children: /*#__PURE__*/_jsx(ClickAwayListener, {
        onClickAway: handleNotificationsClose,
        children: /*#__PURE__*/_jsxs(Paper, {
          sx: {
            p: 1,
            minWidth: 320,
            maxWidth: 420
          },
          children: [/*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
              py: 1
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "subtitle1",
              sx: {
                fontWeight: 600
              },
              children: "Notifications"
            }), /*#__PURE__*/_jsxs(Box, {
              sx: {
                display: "flex",
                gap: 1
              },
              children: [/*#__PURE__*/_jsx(Button, {
                size: "small",
                variant: "text",
                onClick: handleViewAll,
                children: "View all"
              }), /*#__PURE__*/_jsx(Button, {
                size: "small",
                variant: "text",
                color: "primary",
                onClick: handleNotificationsClear,
                children: "Clear"
              })]
            })]
          }), /*#__PURE__*/_jsx(Divider, {}), notifItems.length === 0 ? /*#__PURE__*/_jsx(Box, {
            sx: {
              p: 2
            },
            children: /*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              color: "text.secondary",
              children: "No notifications yet."
            })
          }) : /*#__PURE__*/_jsx(List, {
            dense: true,
            disablePadding: true,
            children: notifItems.map((n, index) => renderNotifRow(n, {
              mode: "popover",
              index,
              list: notifItems
            }))
          })]
        })
      })
    }), /*#__PURE__*/_jsxs(Dialog, {
      open: allDialogOpen,
      onClose: handleCloseAllDialog,
      fullWidth: true,
      maxWidth: "sm",
      children: [/*#__PURE__*/_jsx(DialogTitle, {
        children: "All notifications"
      }), /*#__PURE__*/_jsx(DialogContent, {
        dividers: true,
        children: allItems.length === 0 ? /*#__PURE__*/_jsx(Typography, {
          variant: "body2",
          color: "text.secondary",
          sx: {
            py: 1
          },
          children: "No notifications yet."
        }) : /*#__PURE__*/_jsx(List, {
          dense: true,
          children: allItems.map((n, index) => renderNotifRow(n, {
            mode: "dialog",
            index,
            list: allItems
          }))
        })
      }), /*#__PURE__*/_jsx(DialogActions, {
        children: /*#__PURE__*/_jsx(Button, {
          onClick: handleCloseAllDialog,
          children: "Close"
        })
      })]
    }), /*#__PURE__*/_jsxs(Dialog, {
      open: changelogOpen,
      onClose: () => setChangelogOpen(false),
      fullWidth: true,
      maxWidth: "md",
      PaperProps: {
        sx: {
          maxHeight: "80vh",
          borderRadius: 3
        }
      },
      children: [/*#__PURE__*/_jsxs(DialogTitle, {
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1
        },
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "h6",
          component: "span",
          sx: {
            fontWeight: 700
          },
          children: "Changelog"
        }), /*#__PURE__*/_jsx(IconButton, {
          onClick: () => setChangelogOpen(false),
          size: "small",
          children: /*#__PURE__*/_jsx(HighlightOffIcon, {})
        })]
      }), /*#__PURE__*/_jsx(DialogContent, {
        dividers: true,
        sx: {
          overflowY: "auto",
          px: 3,
          py: 2
        },
        children: Object.entries(changelogData).sort(([a], [b]) => b.localeCompare(a)).map(([date, entry], idx, arr) => {
          const isMinor = Array.isArray(entry);
          const isWip = !isMinor && typeof entry === "object" && entry.type === "wip";
          const isDateExpanded = expandedDates[date] ?? (!isMinor && !isWip && idx === arr.findIndex(([, e]) => !Array.isArray(e) && (typeof e !== "object" || e.type !== "wip")));
          const datePrefix = date.split(" ")[0];
          const parsedDate = new Date(datePrefix + "T00:00:00");
          const formattedDate = isNaN(parsedDate.getTime()) ? date : parsedDate.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
          });
          const headerColor = isWip ? gray.primary : blue.primary;
          return /*#__PURE__*/_jsxs(Box, {
            children: [/*#__PURE__*/_jsxs(Box, {
              sx: {
                mb: 2,
                mt: idx === 0 ? 0 : 2
              },
              children: [/*#__PURE__*/_jsxs(Box, {
                onClick: () => toggleDate(date),
                sx: {
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": {
                    opacity: 0.8
                  }
                },
                children: [/*#__PURE__*/_jsx(ExpandMoreIcon, {
                  sx: {
                    fontSize: "1.4rem",
                    color: headerColor,
                    transform: isDateExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s",
                    mr: 0.5,
                    flexShrink: 0
                  }
                }), /*#__PURE__*/_jsx(Typography, {
                  variant: "subtitle1",
                  sx: {
                    fontWeight: 700,
                    color: headerColor
                  },
                  children: isMinor ? /*#__PURE__*/_jsxs(_Fragment, {
                    children: [formattedDate, /*#__PURE__*/_jsx(Typography, {
                      component: "span",
                      variant: "body2",
                      sx: {
                        ml: 1,
                        fontWeight: 400,
                        color: gray.dark
                      },
                      children: "(minor changes)"
                    })]
                  }) : isWip ? /*#__PURE__*/_jsxs(_Fragment, {
                    children: ["WORK IN PROGRESS", entry.suffix && /*#__PURE__*/_jsx(Typography, {
                      component: "span",
                      variant: "body2",
                      sx: {
                        ml: 1,
                        fontWeight: 400,
                        color: gray.dark
                      },
                      children: entry.suffix
                    })]
                  }) : /*#__PURE__*/_jsxs(_Fragment, {
                    children: ["MAJOR RELEASE \u2014 ", formattedDate]
                  })
                })]
              }), /*#__PURE__*/_jsx(Collapse, {
                in: isDateExpanded,
                timeout: "auto",
                children: isMinor ? /*#__PURE__*/_jsx(Box, {
                  component: "ul",
                  sx: {
                    m: 0,
                    pl: 2.5,
                    mt: 0.5
                  },
                  children: entry.map((item, i) => /*#__PURE__*/_jsx(Box, {
                    component: "li",
                    sx: {
                      fontSize: "0.85rem",
                      lineHeight: 1.6,
                      color: gray.dark
                    },
                    children: item
                  }, i))
                }) : /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsx(Typography, {
                    variant: "body2",
                    sx: {
                      color: gray.dark,
                      fontStyle: "italic",
                      mb: entry.technicalSummary ? 0.5 : 1.5,
                      mt: 0.5
                    },
                    children: entry.summary
                  }), entry.technicalSummary && (() => {
                    const techKey = `${date}-technical`;
                    const isTechExpanded = !!expandedSections[techKey];
                    return /*#__PURE__*/_jsxs(Box, {
                      sx: {
                        mb: 1.5
                      },
                      children: [/*#__PURE__*/_jsxs(Typography, {
                        variant: "body2",
                        onClick: () => toggleSection(techKey),
                        sx: {
                          color: blue.primary,
                          cursor: "pointer",
                          userSelect: "none",
                          fontSize: "0.8rem",
                          "&:hover": {
                            textDecoration: "underline"
                          }
                        },
                        children: [isTechExpanded ? "Hide" : "Show", " ", "technical details"]
                      }), /*#__PURE__*/_jsx(Collapse, {
                        in: isTechExpanded,
                        timeout: "auto",
                        children: /*#__PURE__*/_jsx(Typography, {
                          variant: "body2",
                          sx: {
                            color: gray.dark,
                            fontSize: "0.8rem",
                            lineHeight: 1.6,
                            mt: 0.5
                          },
                          children: entry.technicalSummary
                        })
                      })]
                    });
                  })(), Object.entries(entry.changes).map(([pageType, subPages]) => {
                    const sectionKey = `${date}-${pageType}`;
                    const isExpanded = !!expandedSections[sectionKey];
                    const typeColor = getTypeColor(pageType);
                    const typeSummary = entry.summaries?.[pageType];
                    return /*#__PURE__*/_jsxs(Box, {
                      sx: {
                        mb: 1.5
                      },
                      children: [/*#__PURE__*/_jsxs(Box, {
                        onClick: () => toggleSection(sectionKey),
                        sx: {
                          display: "flex",
                          alignItems: "flex-start",
                          cursor: "pointer",
                          userSelect: "none",
                          "&:hover": {
                            opacity: 0.8
                          }
                        },
                        children: [/*#__PURE__*/_jsx(ExpandMoreIcon, {
                          sx: {
                            fontSize: "1.2rem",
                            color: typeColor,
                            transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 0.2s",
                            mr: 0.5,
                            mt: 0.25,
                            flexShrink: 0
                          }
                        }), /*#__PURE__*/_jsxs(Box, {
                          children: [/*#__PURE__*/_jsx(Typography, {
                            variant: "subtitle2",
                            component: "span",
                            sx: {
                              fontWeight: 600,
                              textTransform: "capitalize",
                              color: typeColor
                            },
                            children: pageType
                          }), typeSummary && /*#__PURE__*/_jsx(Typography, {
                            variant: "body2",
                            sx: {
                              color: gray.dark,
                              mt: 0.25,
                              lineHeight: 1.5
                            },
                            children: typeSummary
                          })]
                        })]
                      }), /*#__PURE__*/_jsx(Collapse, {
                        in: isExpanded,
                        timeout: "auto",
                        children: Object.entries(subPages).map(([subPage, changes]) => /*#__PURE__*/_jsxs(Box, {
                          sx: {
                            ml: 2,
                            mb: 1
                          },
                          children: [/*#__PURE__*/_jsx(Typography, {
                            variant: "body2",
                            sx: {
                              fontWeight: 600,
                              textTransform: "capitalize",
                              color: gray.dark,
                              mb: 0.25
                            },
                            children: subPage.replace(/_/g, " ")
                          }), /*#__PURE__*/_jsx(Box, {
                            component: "ul",
                            sx: {
                              m: 0,
                              pl: 2.5
                            },
                            children: changes.map((change, i) => /*#__PURE__*/_jsx(Box, {
                              component: "li",
                              sx: {
                                fontSize: "0.85rem",
                                lineHeight: 1.6,
                                color: gray.dark
                              },
                              children: change
                            }, i))
                          })]
                        }, subPage))
                      })]
                    }, pageType);
                  })]
                })
              })]
            }), idx < arr.length - 1 && /*#__PURE__*/_jsx(Divider, {
              sx: {
                my: 2
              }
            })]
          }, date);
        })
      }), /*#__PURE__*/_jsx(DialogActions, {
        sx: {
          px: 3,
          py: 1.5
        },
        children: /*#__PURE__*/_jsx(Button, {
          onClick: () => setChangelogOpen(false),
          variant: "outlined",
          size: "small",
          children: "Close"
        })
      })]
    }), /*#__PURE__*/_jsx(ProfileDetailsModal, {
      open: profileModalOpen,
      profile: profileDetails,
      profileImageUrl: user?.profileImageUrl,
      isSaving: profileUpdating,
      error: profileError,
      onClose: closeProfileModal,
      onSave: updateProfile,
      onImageUploaded: () => {
        void handleProfileImageUpdated();
      }
    })]
  });
  return {
    notifCount,
    profileMenuOpen,
    openMenu: handleProfileMenuOpen,
    closeMenu: handleProfileMenuClose,
    elements
  };
}